import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";

/*COMPONENT*/
import Modal from "../common_modal/Modal";
import TableBody from "../mini_data_table/TableBody";
import TableHeader from "../mini_data_table/TableHeader";
import useVirtualScroll from "../mini_data_table/useVirtualScroll";
import useSearchTable from "../mini_data_table/useSearchTable";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import GENERATE_ROW_BULK from "../editor_table/GENERATE_ROW_BULK";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";
import { push_child_array } from "../../App/actions/crudNestedTable";

/*FUNCTION*/
import generate_uuid from "../../App/validation/generate_uuid";

export default function C_E_TABLE({ config, onClose }) {
  // =========================================================================
  // 1. ALL REACT HOOKS MUST BE DECLARED HERE
  // =========================================================================
  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  const layer = useSelector((state) => state.layer);
  const properties = useSelector((state) => state.properties);

  const [data_list, setDataList] = useState([]);
  const [colWidths, setColWidths] = useState({});
  const [fields_child, setFieldsChild] = useState([]);
  const [keyword_state, set_keyword_state] = useState("");
  const [applied_keyword, set_applied_keyword] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [modal_bulk, set_modal_bulk] = useState(false);

  const row = config?.row;
  const field = config?.field;
  const isNested = config?.isNested;
  const child_array = config?.child_array;

  const feature_key_nested_table = properties?.feature_key_nested_table;
  const parent_uuid = field?.uuid;
  const child_uuid = row?.child_uuid || "";

  useEffect(() => {
    if (row && row.type === "Feature") {
      dispatch(
        set_value_properties({
          key: "feature_key_nested_table",
          value: row.key,
        }),
      );
    }
  }, [dispatch, row]);

  const child_array_state = useMemo(() => {
    const current_geo_layer = layer?.geo_layer_list?.find(
      (item) => item?.geo_layer?._id === layer.layer_id,
    )?.geo_layer;

    let feature_key = isNested ? feature_key_nested_table : row?.key;

    const feature = current_geo_layer?.geojson?.features?.find(
      (f) => f.key === feature_key,
    );

    const live_child_array = feature?.child_array || child_array || [];

    return live_child_array.filter(
      (item) => (item?.parent_uuid_temp || "") === child_uuid,
    );
  }, [
    layer,
    row?.key,
    child_uuid,
    child_array,
    feature_key_nested_table,
    isNested,
  ]);

  const on_render = useCallback(() => {
    const { layer_id, geo_layer_list } = layer;
    const current_geo_layer = geo_layer_list?.find(
      (item) => item?.geo_layer?._id === layer_id,
    )?.geo_layer;

    const fields = current_geo_layer?.fields || [];
    const filteredFields = fields.filter(
      (item) => item?.parent_uuid === parent_uuid,
    );

    const newColWidths = {};
    filteredFields.forEach((f) => {
      newColWidths[f.key] = f.width ? Number(f.width) : 160;
    });

    const new_data_list = (child_array_state || []).map((child, idx) => {
      return {
        ...child,
        id: child.child_uuid || `nested-row-${idx}-${Date.now()}`,
        key: child.child_uuid || child.key || `nested-key-${idx}`,
        properties: child.properties || {},
      };
    });

    setDataList(new_data_list);
    setColWidths(newColWidths);
    setFieldsChild(filteredFields);
  }, [child_array_state, layer, parent_uuid]);

  useEffect(() => {
    on_render();
  }, [on_render]);

  const tableHeight = 400;
  const rowHeight = 35;
  const headerHeight = 35;

  const filtered_data_list = useSearchTable({
    data: data_list || [],
    keyword: applied_keyword,
  });

  // FIX 1: Pass the fields and colWidths to the virtualizer so it can compute visibleFields!
  const {
    visibleRows,
    visibleFields,
    totalWidth,
    offsetY,
    handleScroll,
    totalHeight,
  } = useVirtualScroll({
    data: filtered_data_list || [],
    fields: fields_child, // ADDED
    colWidths: colWidths, // ADDED
    rowHeight,
    height: tableHeight,
    containerRef: scrollRef, // ADDED
  });

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // =========================================================================
  // 2. EARLY RETURN
  // =========================================================================
  if (!config) return null;

  // =========================================================================
  // 3. STANDARD FUNCTIONS AND VARIABLES
  // =========================================================================
  const isMenuOpen = Boolean(anchorEl);

  const handleResizeColumn = (key, width) => {
    setColWidths((prev) => ({ ...prev, [key]: width }));
  };

  const handleLocalDataUpdate = (rowId, fieldKey, newValue) => {
    setDataList((prev) =>
      prev.map((r) => {
        if (r.id === rowId) {
          return {
            ...r,
            properties: {
              ...r.properties,
              [fieldKey]: newValue,
            },
          };
        }
        return r;
      }),
    );
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const on_push_child_array = () => {
    const feature_key = isNested ? feature_key_nested_table : row.key;
    const body = {
      geo_layer_id: layer.layer_id,
      feature_key: feature_key,
      child_uuid: generate_uuid(),
      parent_uuid: parent_uuid,
      parent_uuid_temp: child_uuid,
      properties: {},
    };

    dispatch(push_child_array(body));
    handleMenuClose();
  };

  const toggle_bulk_modal = () => set_modal_bulk((prev) => !prev);

  const on_open_bulk = () => {
    set_modal_bulk(true);
    handleMenuClose();
  };

  return (
    <Modal
      modalSize="large"
      id="modal-nested-table"
      isOpen={true}
      onClose={onClose}
      title={field?.name || "Nested Table"}
    >
      <div
        className="box-body"
        style={{
          height: `${tableHeight + 50}px`,
          display: "flex",
          flexDirection: "column",
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* <-- SEARCH BAR UI --> */}
        <div style={{ paddingBottom: "12px", flexShrink: 0, display: "flex" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "30px",
              border: "1px solid #cbd1d8",
              borderRadius: "6px",
              padding: "0 8px",
              gap: "6px",
              backgroundColor: "#fff",
              boxSizing: "border-box",
              width: "260px",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#515151"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              autoFocus
              placeholder="Cari data"
              value={keyword_state}
              onChange={(e) => set_keyword_state(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") set_applied_keyword(keyword_state);
              }}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                height: "100%",
                width: "100%",
                padding: 0,
                margin: 0,
                fontSize: "13px",
                color: "#515151",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        {/* <-- TABLE CONTAINER --> */}
        {/* FIX 2: Removed illegal <table> tag. Now matching parent architecture with a relative scroll container and phantom div */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="table_parent_scroll_container"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "auto",
            minHeight: 0,
            height: "100%",
            borderTop: "none",
            border: "none",
            fontSize: "13px",
            color: "#334155",
            paddingBottom: "80px",
            position: "relative", // Required for absolute positioning of virtual cells
          }}
        >
          {/* Phantom Div (Calculates total scrollbar height) */}
          <div
            className="table_parent_phantom"
            style={{ height: totalHeight + headerHeight, width: totalWidth }}
          />

          {/* 1. STICKY HEADER */}
          <div
            role="table"
            className="table_parent_header_wrapper"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
            }}
          >
            <TableHeader
              visibleFields={visibleFields || []} // Safely fall back to empty array
              totalWidth={totalWidth || 0}
              fields={fields_child}
              colWidths={colWidths}
              onResize={handleResizeColumn}
              parent_uuid={parent_uuid}
            />
          </div>

          {/* 2. VIRTUALIZED BODY */}
          <div
            role="table"
            className="table_parent_body_wrapper"
            style={{
              position: "absolute",
              left: 0,
              top: headerHeight,
              transform: `translateY(${offsetY}px) translateZ(0)`,
            }}
          >
            <TableBody
              isNested={true}
              data={visibleRows}
              fullData={filtered_data_list || []}
              visibleFields={visibleFields || []} // Safely fall back to empty array
              totalWidth={totalWidth || 0}
              fields={fields_child}
              colWidths={colWidths}
              onLocalDataUpdate={handleLocalDataUpdate}
              rowHeight={rowHeight}
              offsetY={offsetY}
              totalHeight={totalHeight}
              containerRef={scrollRef}
            />
          </div>

          <button
            className="button_big background_grey_light outline_white"
            style={{
              position: "fixed", // Changed to fixed to stick to the modal viewport
              bottom: "30px",
              right: "30px",
              zIndex: 20,
              width: "51px",
              fontSize: "21px",
            }}
            onClick={handleMenuOpen}
          >
            +
          </button>

          <FLOAT_MENU
            id="nested-table-float-menu"
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
          >
            <button
              className="button button_white outline_grey margin_bottom"
              onClick={on_push_child_array}
            >
              Tambah baris
            </button>
            <br />
            <button
              className="button button_white outline_grey margin_bottom"
              onClick={on_open_bulk}
            >
              Tambah baris aritmatika
            </button>
          </FLOAT_MENU>
        </div>
      </div>

      {modal_bulk && (
        <Modal
          modalSize="small"
          id="modal_bulk"
          isOpen={modal_bulk}
          onClose={toggle_bulk_modal}
          title="Tambah Baris Aritmatika"
        >
          <div
            className="box-body"
            id="box-body"
            style={{ overflowY: "auto", maxHeight: "80vh" }}
          >
            <GENERATE_ROW_BULK
              feature_key={isNested ? feature_key_nested_table : row?.key}
              parent_uuid={parent_uuid}
              parent_uuid_temp={child_uuid}
              child_fields_shown={data_list || []}
              toggle_generate_row_bulk={toggle_bulk_modal}
            />
          </div>
        </Modal>
      )}
    </Modal>
  );
}
